import i18n from "@/i18n";
import SocketController from "@/services/Socket/socket.controller";
import {
  DashboardCountResponse,
  dashboardCountResponseSchema,
  FullSingleOrder,
  fullSingleOrderSchema,
} from "@/types";
import { validateWithSchema } from "@/utils/dataValidators.zod";

export async function fetchSingleOrder(orderID: string): Promise<
  | {
      rStatus: "success";
      rData: FullSingleOrder;
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "orderByID",
      { orderID },
      true
    );

    if (response.status === 200) {
      const validatedData = validateWithSchema<FullSingleOrder>(
        response.data,
        fullSingleOrderSchema,
        "Full Single Order"
      );

      if (validatedData) {
        return {
          rStatus: "success",
          rData: validatedData,
        };
      } else {
        console.error("Invalid single order data received", response.data);
        return {
          rStatus: "error",
          rData: i18n.t("Invalid single order data received"),
        };
      }
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("No order found with the provided ID"),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch single order"),
    };
  } catch (error) {
    console.error("Error fetching single order:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch single order"),
    };
  }
}

export async function fetchDashboardCounts(): Promise<
  | {
      rStatus: "success";
      rData: DashboardCountResponse;
    }
  | {
      rStatus: "error";
      rData: string;
    }
> {
  try {
    const response = await SocketController.prototype.sendCommand(
      "userDashboard",
      {},
      true
    );

    if (response.status === 200) {
      const validatedData = validateWithSchema<DashboardCountResponse>(
        response.data,
        dashboardCountResponseSchema,
        "Dashboard Count Response"
      );

      if (validatedData) {
        return {
          rStatus: "success",
          rData: validatedData,
        };
      } else {
        console.error("Invalid dashboard counts data received", response.data);
        return {
          rStatus: "error",
          rData: i18n.t("Invalid dashboard counts data received"),
        };
      }
    }

    if (response.status === 204) {
      return {
        rStatus: "error",
        rData: i18n.t("No data available for dashboard counts"),
      };
    }

    if (response.status === 0) {
      return {
        rStatus: "error",
        rData: i18n.t("Network error or timeout"),
      };
    }

    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch dashboard counts"),
    };
  } catch (error) {
    console.error("Error fetching dashboard counts:", error);
    return {
      rStatus: "error",
      rData: i18n.t("Failed to fetch dashboard counts"),
    };
  }
}
